import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useOnboardingState } from '@/contexts/OnboardingContext';
import { CAPS } from '@/lib/kbCaps';
import { extractKbFieldErrors, type FieldErrorMap } from '@/lib/kbFieldErrors';
import { FieldError } from '@/components/onboarding/enrichment/FieldError';
import { CharCounter } from '@/components/shared/CharCounter';
import type { KbProfileUpdateRequest } from '@/services/kbProfileService';

const TONE_OPTIONS = ['formal', 'casual', 'friendly', 'professional', 'direct'] as const;

/** F193 — per-tenant welcome opener shape; 'long' is the default/current behaviour. */
const WELCOME_STYLE_OPTIONS = ['long', 'short'] as const;
type WelcomeStyle = (typeof WELCOME_STYLE_OPTIONS)[number];

interface VoiceEditorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialTone: string | null;
  initialVoiceExample: string | null;
  /** F193 — absent (e.g. a pre-migration profile response) reads as 'long'. */
  initialWelcomeStyle?: WelcomeStyle;
}

export function VoiceEditor({
  open,
  onOpenChange,
  initialTone,
  initialVoiceExample,
  initialWelcomeStyle,
}: VoiceEditorProps) {
  const { t } = useTranslation();
  const { updateKbProfile } = useOnboardingState();
  const [tone, setTone] = useState(initialTone ?? '');
  const [voiceExample, setVoiceExample] = useState(initialVoiceExample ?? '');
  const [welcomeStyle, setWelcomeStyle] = useState<WelcomeStyle>(initialWelcomeStyle ?? 'long');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrorMap | null>(null);

  async function handleSave() {
    setSaving(true);
    setError(null);
    setFieldErrors(null);

    // F193 — only send keys whose normalised value actually changed (the
    // backend accepts partial PATCH — F036). An unchanged field still calls
    // updateKbProfile (possibly with `{}`) so "Save" always closes the
    // dialog and refreshes the profile the same way regardless of which
    // fields moved.
    const payload: KbProfileUpdateRequest = {};

    const normalizedTone = tone || null;
    if (normalizedTone !== (initialTone ?? null)) {
      payload.tone = normalizedTone;
    }

    const normalizedVoiceExample = voiceExample || null;
    if (normalizedVoiceExample !== (initialVoiceExample ?? null)) {
      payload.voice_example = normalizedVoiceExample;
    }

    if (welcomeStyle !== (initialWelcomeStyle ?? 'long')) {
      payload.welcome_style = welcomeStyle;
    }

    try {
      await updateKbProfile(payload);
      onOpenChange(false);
    } catch (err) {
      const fe = extractKbFieldErrors(err);
      if (fe) {
        setFieldErrors(fe);
      } else {
        setError(t('common.error_generic'));
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('bot.enrich.voice.title')}</DialogTitle>
          <DialogDescription>{t('bot.enrich.voice.description')}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="voice-tone">{t('bot.enrich.voice.tone_label')}</Label>
            <Select value={tone} onValueChange={setTone}>
              <SelectTrigger id="voice-tone" className="min-h-11">
                <SelectValue placeholder={t('bot.enrich.voice.tone_placeholder')} />
              </SelectTrigger>
              <SelectContent>
                {TONE_OPTIONS.map((opt) => (
                  <SelectItem key={opt} value={opt}>
                    {t(`bot.enrich.voice.tone_${opt}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="voice-example">{t('bot.enrich.voice.example_label')}</Label>
            <Textarea
              id="voice-example"
              value={voiceExample}
              onChange={(e) => setVoiceExample(e.target.value)}
              placeholder={t('bot.enrich.voice.example_placeholder')}
              rows={3}
              maxLength={CAPS.voice_example}
            />
            <CharCounter value={voiceExample} max={CAPS.voice_example} />
            <FieldError errors={fieldErrors} path="voice_example" />
          </div>

          <div className="space-y-2">
            <Label>{t('bot.enrich.voice.welcome_style_label')}</Label>
            <RadioGroup
              value={welcomeStyle}
              onValueChange={(value) => setWelcomeStyle(value as WelcomeStyle)}
              className="gap-2"
            >
              {WELCOME_STYLE_OPTIONS.map((opt) => (
                <Label
                  key={opt}
                  htmlFor={`voice-welcome-style-${opt}`}
                  className="flex min-h-11 cursor-pointer items-center gap-3 rounded-md border border-input px-3 py-2 font-normal"
                >
                  <RadioGroupItem id={`voice-welcome-style-${opt}`} value={opt} />
                  <span className="text-sm">{t(`bot.enrich.voice.welcome_style_${opt}`)}</span>
                </Label>
              ))}
            </RadioGroup>
            <p className="text-xs text-muted-foreground">
              {t('bot.enrich.voice.welcome_style_help')}
            </p>
          </div>

          {error && (
            <p className="text-sm text-destructive" role="alert">
              {error}
            </p>
          )}
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saving}
            className="min-h-11"
          >
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSave} disabled={saving} className="min-h-11">
            {t('common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
